import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/utils/apiClient";
import { useAuth } from "@/state/auth";

export interface WidgetConfigRecord {
  settingId: string;
  publicKey: string | null;
  overrides: Record<string, unknown>;
  updatedAt: string | null;
  updatedBy: string | null;
}

export const useWidgetConfigList = () => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: ["admin", "widget-configs"],
    queryFn: async () => {
      const { configs } = await apiRequest<{ configs: WidgetConfigRecord[] }>("/api/admin/widget/configs");
      return configs;
    },
    staleTime: 5 * 60_000,
    enabled: Boolean(profile),
  });
};

export const useWidgetConfig = (publicKey: string | null) => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: ["admin", "widget-config", publicKey ?? "default"],
    queryFn: async () => {
      const key = publicKey ?? "default";
      const { config } = await apiRequest<{ config: { publicKey: string | null; overrides: Record<string, unknown> } }>(
        `/api/admin/widget/configs/${encodeURIComponent(key)}`,
      );
      return config;
    },
    enabled: Boolean(profile),
  });
};

export const useUpdateWidgetConfig = () => {
  const profile = useAuth((state) => state.profile);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      publicKey,
      overrides,
    }: {
      publicKey: string | null;
      overrides: Record<string, unknown>;
    }) => {
      const key = publicKey ?? "default";
      const { config } = await apiRequest<{ config: WidgetConfigRecord }>(
        `/api/admin/widget/configs/${encodeURIComponent(key)}`,
        {
          method: "PUT",
          body: JSON.stringify({ overrides }),
        },
      );
      return config;
    },
    onSuccess: (config) => {
      if (profile) {
        queryClient.invalidateQueries({ queryKey: ["admin", "widget-configs"] });
        queryClient.invalidateQueries({ queryKey: ["admin", "widget-config", config.publicKey ?? "default"] });
      }
    },
  });
};

export const useDeleteWidgetConfig = () => {
  const profile = useAuth((state) => state.profile);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (publicKey: string | null) => {
      const key = publicKey ?? "default";
      await apiRequest<void>(`/api/admin/widget/configs/${encodeURIComponent(key)}`, {
        method: "DELETE",
      });
      return key;
    },
    onSuccess: (key) => {
      if (profile) {
        queryClient.invalidateQueries({ queryKey: ["admin", "widget-configs"] });
        queryClient.invalidateQueries({ queryKey: ["admin", "widget-config", key] });
      }
    },
  });
};
